import { Directive, ElementRef, EventEmitter, OnInit, OnDestroy, Output, HostListener } from '@angular/core';
import Hammer from 'hammerjs';

@Directive({
  selector: '[swipeleft], [swiperight], [swipe]',
  standalone: false
})
export class SwipeDirective implements OnInit, OnDestroy {
  @Output() swipeleft = new EventEmitter<any>();
  @Output() swiperight = new EventEmitter<any>();
  @Output() swipe = new EventEmitter<any>();

  private mc: HammerManager | null = null;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private lastFiredTime = 0;
  private isPinching = false;

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined' && this.el.nativeElement) {
      try {
        this.mc = new Hammer(this.el.nativeElement, {
          touchAction: 'auto'
        });
        this.mc.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 15, velocity: 0.2 });

        if (this.el.nativeElement.style) {
          this.el.nativeElement.style.touchAction = 'pan-y pinch-zoom';
        }

        this.mc.on('swipeleft', (event: any) => {
          this.handleSwipe('left', event);
        });

        this.mc.on('swiperight', (event: any) => {
          this.handleSwipe('right', event);
        });

        this.mc.on('swipe', (event: any) => {
          this.swipe.emit(event);
        });
      } catch (e) {
        console.warn('HammerJS init error:', e);
      }
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(e: TouchEvent) {
    if (e.touches && e.touches.length > 1) {
      // Multi-touch gesture (e.g. pinch-to-zoom) -> do not track as swipe
      this.isPinching = true;
      this.touchStartX = 0;
      this.touchStartY = 0;
      return;
    }
    if (e.touches && e.touches.length === 1) {
      this.isPinching = false;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartTime = Date.now();
    }
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(e: TouchEvent) {
    if (e.touches && e.touches.length > 1) {
      this.isPinching = true;
      this.touchStartX = 0;
      this.touchStartY = 0;
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(e: TouchEvent) {
    if (this.isPinching) {
      if (!e.touches || e.touches.length === 0) {
        this.isPinching = false;
      }
      this.touchStartX = 0;
      this.touchStartY = 0;
      return;
    }
    if (!this.touchStartX || !e.changedTouches || e.changedTouches.length === 0) return;
    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const diffX = endX - this.touchStartX;
    const diffY = endY - this.touchStartY;
    const timeDiff = Date.now() - this.touchStartTime;

    this.touchStartX = 0;
    this.touchStartY = 0;

    const target = e.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }

    if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY) * 1.2 && timeDiff < 800) {
      if (diffX > 0) {
        this.handleSwipe('right', { type: 'swiperight', deltaX: diffX, deltaY: diffY });
      } else {
        this.handleSwipe('left', { type: 'swipeleft', deltaX: diffX, deltaY: diffY });
      }
    }
  }

  private handleSwipe(direction: 'left' | 'right', event: any) {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    if (event?.srcEvent?.stopPropagation) {
      event.srcEvent.stopPropagation();
    }
    const now = Date.now();
    // Debounce to prevent double-firing from Hammer + native touch
    if (now - this.lastFiredTime < 400) return;
    this.lastFiredTime = now;

    if (direction === 'left') {
      this.swipeleft.emit(event);
    } else {
      this.swiperight.emit(event);
    }
    this.swipe.emit(event);
  }

  ngOnDestroy(): void {
    if (this.mc) {
      this.mc.destroy();
      this.mc = null;
    }
  }
}
